<script setup lang="ts">
import type { Component } from 'vue'

import type { PlanType } from '@/api/interfaces'
import { getImageUrl } from '@/helpers/utils'

import LockIcon from '@/components/icons/LockIcon.vue'

interface Props {
  items: RadioItem[]
  disabled?: boolean
  stacked?: boolean
}

export interface RadioItem {
  label: string
  value: string | number | boolean
  imageUrl?: string
  component?: Component
  planType?: PlanType
  disabled?: boolean
}

const model = defineModel()
defineProps<Props>()
const upgradePlanUrl = `${import.meta.env.VITE_FRONTEND_URL}/upgrade-plan`

const isActive = (item: RadioItem) => model.value === item.value
const selectItem = (item: RadioItem) => {
  if (item.disabled) {
    return
  }

  model.value = item.value
}
</script>

<template>
  <div :class="['gs-radio-group', { disabled, stacked }]">
    <v-radio-group v-model="model" :disabled="disabled">
      <template v-for="(item, index) in items" :key="index">
        <div
          class="gs-radio-group-item"
          :class="{ active: isActive(item), disabled: item.disabled }"
          @click="selectItem(item)"
        >
          <v-radio :label="item.label" :value="item.value" />
          <div class="gs-radio-group-content">
            <div v-if="item.imageUrl" class="gs-radio-group-image">
              <img :src="getImageUrl(item.imageUrl)" :alt="item.label" />
            </div>
            <component v-if="item.component" :is="item.component" />
          </div>

          <a
            v-if="item.planType"
            :href="upgradePlanUrl"
            target="_blank"
            class="gs-radio-group-item-overlay"
          >
            <LockIcon />
            {{ $t(`EditorSettings.upgradePlan.${item.planType}`) }}
          </a>
        </div>
      </template>
    </v-radio-group>
  </div>
</template>

<style lang="scss" scoped>
.gs-radio-group {
  :deep(.v-selection-control-group) {
    gap: 20px;

    .v-label {
      opacity: 1;
    }
  }

  &.disabled {
    opacity: 0.7;
  }

  &.stacked {
    :deep(.v-selection-control-group) {
      gap: 8px;
      flex-direction: row;
      flex-wrap: wrap;
    }

    .gs-radio-group-item {
      max-width: 155px;
      padding: 4px 12px 12px;
    }
  }
}

.gs-radio-group-item {
  border-radius: 10px;
  border: 1px solid var(--gray-light-dd);
  padding: 8px 16px 16px;
  max-width: 305px;
  cursor: pointer;
  transition: all 100ms ease-in-out;

  &.active {
    border-color: var(--ui-emerald-green);
    background-color: #47d18f33;
  }

  &.disabled {
    position: relative;

    .gs-radio-group-item-overlay {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 12px;
      width: 100%;
      height: 100%;
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-color: var(--ui-black);
      opacity: 0.7;
      color: var(--lightest);
      border-radius: 10px;
      text-decoration: none;
      font-size: 18px;
      font-weight: 700;
    }
  }
}

.gs-radio-group-item-overlay {
  display: none;
}

.gs-radio-group-image {
  display: flex;
  align-items: center;

  img {
    object-fit: cover;
  }
}
</style>
